import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";

pdfMake.vfs = pdfFonts.pdfMake?.vfs || pdfFonts.vfs || pdfFonts;

// The two documents a hospital produces on a death, rendered client-side (the
// backend has no PDF engine) from the current DischargeProcess row:
//
//   downloadMccdForm4    — the Medical Certificate of Cause of Death (Form 4).
//                          Carries the full cause sequence; goes to the Registrar.
//   downloadRelativeSlip — the slip handed to the family. Carries NO cause of
//                          death (RBD s.17 forbids the cause on any certificate
//                          given to a person).
//
// `context` supplies the identity/hospital fields the DischargeProcess row does
// not: { patientName, uhid, sex, age, ward, hospitalName, hospitalAddress,
// admittedAt }.

function fmtDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
}

function fmtDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
}

function ageText(age) {
  if (age == null || age === "") return "—";
  const n = Number(age);
  return Number.isFinite(n) ? `${n} years` : String(age);
}

const MANNER_LABELS = {
  natural: "Natural",
  accident: "Accident",
  suicide: "Suicide",
  homicide: "Homicide",
  pending_investigation: "Pending investigation",
};

function labeled(label, value) {
  return {
    columns: [
      { width: 130, text: label, bold: true, fontSize: 10 },
      { width: "*", text: value ?? "—", fontSize: 10 },
    ],
    margin: [0, 1],
  };
}

// The Part I / Part II cause table.
function causeTable(dp) {
  const part1 = Array.isArray(dp.mccd_part1) ? dp.mccd_part1 : [];
  const part2 = Array.isArray(dp.mccd_part2) ? dp.mccd_part2 : [];
  const body = [
    [
      { text: "Cause of Death", style: "th" },
      { text: "Interval between onset and death", style: "th" },
    ],
  ];

  const PART1_PREFIX = ["(a)", "due to (b)", "due to (c)"];
  part1.forEach((row, i) => {
    body.push([
      { text: `${PART1_PREFIX[i] || ""} ${row.condition || ""}`.trim(), fontSize: 10 },
      { text: row.interval || "—", fontSize: 10 },
    ]);
  });
  if (!part1.length) {
    body.push([{ text: "(not recorded)", italics: true, colSpan: 2, fontSize: 10 }, {}]);
  }

  body.push([
    { text: "Part II — other significant conditions", style: "th", colSpan: 2 },
    {},
  ]);
  if (part2.length) {
    part2.forEach((row) => {
      body.push([
        { text: row.condition || "", fontSize: 10 },
        { text: row.interval || "—", fontSize: 10 },
      ]);
    });
  } else {
    body.push([{ text: "—", colSpan: 2, fontSize: 10 }, {}]);
  }

  return {
    table: { widths: ["*", 150], body },
    layout: "lightHorizontalLines",
    margin: [0, 6],
  };
}

export function downloadMccdForm4(dp, context = {}) {
  const deathDate = fmtDate(dp.death_datetime);
  const deathTime = dp.death_datetime
    ? new Date(dp.death_datetime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "—";
  const isNonNatural = dp.manner_of_death && dp.manner_of_death !== "natural";

  const doc = {
    pageSize: "A4",
    pageMargins: [44, 48, 44, 48],
    content: [
      context.hospitalName ? { text: context.hospitalName, style: "hospital" } : null,
      context.hospitalAddress ? { text: context.hospitalAddress, style: "hospitalSub" } : null,
      { text: "MEDICAL CERTIFICATE OF CAUSE OF DEATH", style: "title" },
      { text: "(Form No. 4 — Hospital in-patients)", style: "subtitle" },
      dp.mccd_number ? { text: `Certificate No: ${dp.mccd_number}`, style: "certNo" } : null,
      { text: " ", margin: [0, 4] },
      {
        text: `I hereby certify that the person whose particulars are given below died in this hospital in Ward ${
          context.ward || "____"
        } on ${deathDate} at ${deathTime}.`,
        fontSize: 10,
        margin: [0, 0, 0, 8],
      },
      labeled("Name of deceased", context.patientName),
      labeled("UHID", context.uhid),
      labeled("Sex", context.sex),
      labeled("Age at death", ageText(context.age)),
      causeTable(dp),
      labeled("Manner of death", MANNER_LABELS[dp.manner_of_death] || "—"),
      isNonNatural ? labeled("How injury occurred", dp.mccd_injury_description) : null,
      dp.mccd_pregnancy_associated != null
        ? labeled(
            "Pregnancy-associated",
            dp.mccd_pregnancy_associated
              ? `Yes${dp.mccd_delivery_occurred != null ? ` (delivery: ${dp.mccd_delivery_occurred ? "yes" : "no"})` : ""}`
              : "No"
          )
        : null,
      { text: " ", margin: [0, 18] },
      {
        columns: [
          { text: "" },
          {
            width: "50%",
            stack: [
              { text: "_______________________", alignment: "center" },
              { text: dp.certifying_doctor_name || "Medical Attendant", style: "sig" },
              dp.certifying_doctor_qualifications
                ? { text: dp.certifying_doctor_qualifications, style: "sigSub" }
                : null,
              dp.certifying_doctor_registration_no
                ? { text: `Regn. No.: ${dp.certifying_doctor_registration_no}`, style: "sigSub" }
                : null,
              { text: `Date: ${fmtDate(dp.mccd_issued_at)}`, style: "sigSub" },
            ].filter(Boolean),
          },
        ],
      },
      {
        text: "To be sent to the Registrar along with Form No. 2 (Death Report). A copy of this certificate is to be provided to the nearest relative of the deceased.",
        style: "footer",
      },
    ].filter(Boolean),
    styles: {
      hospital: { fontSize: 13, bold: true, alignment: "center" },
      hospitalSub: { fontSize: 9, alignment: "center", color: "#555555", margin: [0, 0, 0, 4] },
      title: { fontSize: 14, bold: true, alignment: "center", margin: [0, 6, 0, 0] },
      subtitle: { fontSize: 9, alignment: "center", color: "#555555" },
      certNo: { fontSize: 9, alignment: "center", color: "#555555", margin: [0, 2] },
      th: { fontSize: 9, bold: true, fillColor: "#f0f0f0" },
      sig: { fontSize: 10, bold: true, alignment: "center", margin: [0, 4, 0, 0] },
      sigSub: { fontSize: 9, color: "#555555", alignment: "center" },
      footer: { fontSize: 8, color: "#777777", margin: [0, 24, 0, 0] },
    },
  };

  pdfMake.createPdf(doc).download(`${dp.mccd_number || `MCCD-${dp.admission_id}`}.pdf`);
}

export function downloadRelativeSlip(dp, context = {}) {
  // Deliberately NO cause of death — RBD s.17 proviso.
  const doc = {
    pageSize: "A5",
    pageMargins: [40, 44, 40, 44],
    content: [
      context.hospitalName ? { text: context.hospitalName, style: "hospital" } : null,
      context.hospitalAddress ? { text: context.hospitalAddress, style: "hospitalSub" } : null,
      { text: "CERTIFICATE OF DEATH", style: "title" },
      { text: " ", margin: [0, 8] },
      {
        text: [
          "Certified that ",
          { text: context.patientName || "____________", bold: true },
          context.uhid ? ` (UHID ${context.uhid})` : "",
          ", was admitted to this hospital on ",
          { text: fmtDate(context.admittedAt), bold: true },
          " and expired on ",
          { text: fmtDateTime(dp.death_datetime), bold: true },
          ".",
        ],
        fontSize: 11,
        lineHeight: 1.5,
      },
      { text: " ", margin: [0, 28] },
      {
        columns: [
          { text: "" },
          {
            width: "55%",
            stack: [
              { text: "_______________________", alignment: "center" },
              { text: dp.certifying_doctor_name || "Medical Superintendent", style: "sig" },
              { text: context.hospitalName || "", style: "sigSub" },
              { text: `Date: ${fmtDate(dp.death_datetime)}`, style: "sigSub" },
            ],
          },
        ],
      },
      {
        text: "This certificate does not state the cause of death and is not the statutory death certificate, which is issued by the Registrar of Births and Deaths.",
        style: "footer",
      },
    ].filter(Boolean),
    styles: {
      hospital: { fontSize: 12, bold: true, alignment: "center" },
      hospitalSub: { fontSize: 8, alignment: "center", color: "#555555", margin: [0, 0, 0, 4] },
      title: { fontSize: 13, bold: true, alignment: "center", margin: [0, 6, 0, 0] },
      sig: { fontSize: 10, bold: true, alignment: "center", margin: [0, 4, 0, 0] },
      sigSub: { fontSize: 8, color: "#555555", alignment: "center" },
      footer: { fontSize: 7.5, color: "#777777", alignment: "center", margin: [0, 24, 0, 0] },
    },
  };

  pdfMake.createPdf(doc).download(`death-slip-${context.uhid || dp.admission_id}.pdf`);
}
